import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Layers, FileCode, Target, Workflow, GitBranch, CheckSquare, PenTool, Share2, Building } from "lucide-react";
import { useLang } from "../context/I18nContext";
import SectionVideo from "../components/SectionVideo";
import { SECTION_VIDEOS } from "../config/sectionVideos";

/**
 * Planung & BIM — Technik-Schema Seite
 * Dark blue, professional, no shop optics.
 */
export default function PlanungBIM() {
  const { t } = useLang();

  const planningScope = [
    "Bestandsaufnahme", "Bedarfsermittlung", "Heizlast", "Warmwasserbedarf",
    "Kühlbedarf", "Anlagenkonzept", "Leitungsführung", "Speichertechnik",
    "Hydraulik", "Regelung", "Energiemanagement", "Smart-Home-Konzept",
    "Schnittstellenplanung", "Produktauswahl", "Kostenoptimierung",
  ];

  const bimLayers = [
    { icon: Layers, label: "Heizung", d: "Heizkreise, Pufferspeicher, Verteiler" },
    { icon: Share2, label: "Sanitär", d: "Warmwasser, Zirkulation, Verteilung" },
    { icon: FileCode, label: "Elektro", d: "Leitungen, Verteiler, Technikräume" },
    { icon: Workflow, label: "Lüftung", d: "Luftführung, Rückgewinnung, Steuerung" },
    { icon: GitBranch, label: "Gebäudeautomation", d: "Bus, Funk, Visualisierung" },
    { icon: Target, label: "Sensorik", d: "Temperatur, Präsenz, Verbrauch" },
  ];

  return (
    <div className="page-shell" data-testid="planung-bim-page">
      {/* Hero */}
      <section className="site-container page-hero">
        <div className="eyebrow mb-6 text-[#5FA637]">01 · {t("nav.planungBim")}</div>
        <h1 className="page-title">
          Planung vor<br/>
          <span className="italic-accent">Ausführung.</span>
        </h1>
        <p className="copy-lead mt-8 text-base md:text-lg">
          Gute Gebäudetechnik beginnt nicht auf der Baustelle, sondern in der Planung. Technische Systeme werden
          bereits vor der Umsetzung digital koordiniert — Schnittstellen, Leitungswege, Platzbedarf und
          Wartungszugänge werden früh berücksichtigt.
        </p>
      </section>

      {/* Section video — BIM / technische Planung */}
      <SectionVideo {...SECTION_VIDEOS.planungBim}/>

      {/* Technische Planung */}
      <section className="site-container section-block border-t border-white/10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 reveal">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-5">Technische Planung</div>
            <h2 className="section-title">
              Planung der gesamten <span className="italic-accent">Haustechnik.</span>
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed">
              Für Wohnung, Haus, Mehrfamilienhaus und Wohnanlage. Die einzelnen technischen Systeme werden
              früh gemeinsam betrachtet — so können Schnittstellen, Leitungswege und technische Anforderungen
              vor der Ausführung berücksichtigt werden.
            </p>
          </div>
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
              {planningScope.map((item, i) => (
                <div key={i} className="bg-[#0A1F38]/65 px-4 py-3.5 flex items-center gap-3 text-sm text-white/80">
                  <CheckSquare size={14} strokeWidth={1.6} className="text-[#5FA637] shrink-0"/>
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* BIM */}
      <section className="site-container section-block border-t border-white/10">
        <div className="mb-12 reveal">
          <div className="eyebrow mb-5">BIM & Koordination</div>
          <h2 className="section-title max-w-4xl">
            Technik wird geplant,<br/><span className="italic-accent">bevor sie gebaut wird.</span>
          </h2>
          <p className="mt-6 text-white/70 max-w-3xl leading-relaxed">
            Technische Anlagen werden digital koordiniert — Wärmepumpen, Heizungsverteilung, Fußbodenheizung,
            Warmwasser, Speicher, Lüftung, Kühlung, Elektro, Beleuchtung, Gebäudeautomation, Leitungen,
            Verteiler und Technikräume.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10 reveal-stagger">
          {bimLayers.map((layer, i) => (
            <div key={i} className="technical-card p-7 min-h-[170px]">
              <div className="flex items-center gap-3 mb-5">
                <layer.icon size={20} strokeWidth={1.6} className="text-[#5FA637]"/>
                <div className="eyebrow text-[#5FA637]/80">Layer</div>
              </div>
              <div className="font-display text-xl mb-2">{layer.label}</div>
              <div className="text-sm text-white/60 leading-relaxed">{layer.d}</div>
            </div>
          ))}
        </div>

        <div className="mt-12 surface-panel p-8 md:p-10">
          <div className="flex items-start gap-4">
            <PenTool size={24} strokeWidth={1.5} className="text-[#5FA637] mt-1 shrink-0"/>
            <div>
              <div className="eyebrow mb-2 text-[#5FA637]">Zielsetzung</div>
              <p className="text-base md:text-lg text-white/85 leading-relaxed max-w-4xl">
                Kollisionen früh erkennen, Platzbedarf prüfen, Wartungszugänge berücksichtigen und Gewerke
                besser koordinieren. <span className="text-white/60">Schnittstellen werden sichtbar, bevor sie
                auf der Baustelle zum Problem werden.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="site-container section-block">
        <div className="cta-panel">
          <div className="eyebrow mb-5 text-[#5FA637]">Nächster Schritt</div>
          <h2 className="section-title max-w-3xl">
            Projekt mit <span className="italic-accent">BIM-Planung</span> starten.
          </h2>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/kontakt" className="btn-primary">
              {t("cta.inquiry")} <ArrowRight size={14}/>
            </Link>
            <Link to="/gebaeudeautomation" className="btn-ghost">
              {t("nav.automation")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
